import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ChevronRight, Sparkles, ArrowRight, ShoppingBag, User, Package, LogOut } from "lucide-react";
import { BRAND } from "../../data/content";
import { PRODUCT_GROUPS, SOLUTION_GROUPS, LOCATION_ITEMS } from "../../data/seoContent";
import { useCart } from "../../shop/CartContext";
import { useCustomerAuth } from "../../shop/AuthContext";
import { useCms } from "../../cms/CmsContext";
import CartDrawer from "../../shop/CartDrawer";

const DEFAULT_NAV_ITEMS = [
  { key: "home", label: "Home", to: "/" },
  { key: "dynamic-series", label: "Dynamic Series", to: "/dynamic-series", highlight: true },
  { key: "value-packs", label: "Smart Home Kits", to: "/value-packs" },
  { key: "shop", label: "Shop", to: "/shop" },
  { key: "about", label: "About", to: "/about" },
  { key: "contact", label: "Contact", to: "/contact" },
];

// Merges Website Content → Global Content → Header nav_items overrides (label/to/enabled)
// over the default nav. Falls back to defaults entirely if CMS is empty/unavailable.
// Merges Website Content → Global Content → Header mega_menus overrides
// (label/enabled) over the defaults. Products/Solutions/Locations link content
// stays code-managed — only the trigger label and show/hide are CMS-editable.
function mergeMegaMenus(cmsMenus) {
  const defaults = { products: { label: "Products", enabled: true }, solutions: { label: "Solutions", enabled: true }, locations: { label: "Locations", enabled: true } };
  if (!cmsMenus) return defaults;
  return {
    products: { ...defaults.products, ...(cmsMenus.products || {}) },
    solutions: { ...defaults.solutions, ...(cmsMenus.solutions || {}) },
    locations: { ...defaults.locations, ...(cmsMenus.locations || {}) },
  };
}

function mergeNavItems(cmsItems) {
  if (!Array.isArray(cmsItems) || cmsItems.length === 0) return DEFAULT_NAV_ITEMS;
  return DEFAULT_NAV_ITEMS.map((d) => {
    const override = cmsItems.find((c) => c.key === d.key);
    if (!override) return d;
    return { ...d, label: override.label || d.label, to: override.to || d.to, enabled: override.enabled };
  });
}

/**
 * MegaNav — Nested mega-dropdown navigation (One Switch style).
 *
 * Desktop:
 *   - Top-level: Products / Solutions / Locations open a panel.
 *   - Inside each panel: a left rail lists categories. Hovering a category
 *     reveals its sub-items in the right pane (proper nested sub-dropdown).
 *
 * Mobile: accordion drawer with sub-accordions.
 */
export default function MegaNav({ onBook }) {
  const [openMenu, setOpenMenu] = useState(null); // "products" | "solutions" | "locations"
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const cart = useCart();
  const auth = useCustomerAuth();
  const { data: cmsData } = useCms();
  const loc = useLocation();
  const navigate = useNavigate();

  const navItems = mergeNavItems(cmsData?.header?.nav_items).filter((i) => i.enabled !== false);
  const preItems = navItems.filter((i) => ["home", "dynamic-series", "value-packs", "shop"].includes(i.key));
  const postItems = navItems.filter((i) => ["about", "contact"].includes(i.key));
  const contact = cmsData?.contact || {};
  const phone = contact.phone || BRAND.phone;
  const phoneRaw = phone.replace(/[^0-9]/g, "");
  const ctaLabel = cmsData?.header?.cta_label || "Experience";
  const ctaLink = cmsData?.header?.cta_link || "/experience";
  const megaMenus = mergeMegaMenus(cmsData?.header?.mega_menus);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpenMenu((v) => (v === null ? v : null));
    setMobileOpen((v) => (v === false ? v : false));
  }, [loc.pathname]);

  return (
    <header
      data-testid="mega-nav"
      className={`fixed top-0 inset-x-0 z-[60] transition-all duration-500 ${
        scrolled || openMenu
          ? "bg-[rgba(7,7,10,0.86)] backdrop-blur-xl border-b border-line/60"
          : "bg-transparent"
      }`}
      onMouseLeave={() => setOpenMenu(null)}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <span className="font-display text-[1.35rem] lg:text-[1.55rem] tracking-tight">
            Genie<span className="text-gold">X</span>
          </span>
          <span className="hidden md:inline text-[10px] tracking-[0.32em] text-mute uppercase pl-3 border-l border-line">
            Intelligent Living
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {preItems.map((i) => (
            <NavItem key={i.key} to={i.to} label={i.label} highlight={i.highlight} onEnter={() => setOpenMenu(null)} />
          ))}
          {megaMenus.products.enabled !== false && (
            <DropdownTrigger
              label={megaMenus.products.label}
              isOpen={openMenu === "products"}
              onEnter={() => setOpenMenu("products")}
              testid="nav-products"
            />
          )}
          {megaMenus.solutions.enabled !== false && (
            <DropdownTrigger
              label={megaMenus.solutions.label}
              isOpen={openMenu === "solutions"}
              onEnter={() => setOpenMenu("solutions")}
              testid="nav-solutions"
            />
          )}
          {megaMenus.locations.enabled !== false && (
            <DropdownTrigger
              label={megaMenus.locations.label}
              isOpen={openMenu === "locations"}
              onEnter={() => setOpenMenu("locations")}
              testid="nav-locations"
            />
          )}
          {postItems.map((i) => (
            <NavItem key={i.key} to={i.to} label={i.label} onEnter={() => setOpenMenu(null)} />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:+${phoneRaw}`}
            data-testid="nav-phone"
            className="hidden md:inline-flex items-center gap-2 text-[12px] text-dim hover:text-gold transition-colors"
          >
            <Phone className="w-3.5 h-3.5" /> {phone}
          </a>
          <Link
            to={ctaLink}
            data-testid="nav-experience-cta"
            className="hidden md:inline-flex btn-gold !py-2.5 !px-5 !text-[11px] items-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5" /> {ctaLabel}
          </Link>
          <button
            type="button"
            onClick={() => setCartOpen(true)}
            aria-label="Cart"
            data-testid="nav-cart-button"
            className="relative w-10 h-10 rounded-full border border-line flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            {cart.count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gold text-black text-[10px] font-semibold flex items-center justify-center"
              >
                {cart.count}
              </span>
            )}
          </button>
          {auth?.user && (
            <div className="relative hidden md:block" onMouseLeave={() => setAcctOpen(false)}>
              <button
                type="button"
                onClick={() => setAcctOpen((v) => !v)}
                aria-label="Account"
                data-testid="nav-account-button"
                className="w-10 h-10 rounded-full border border-line overflow-hidden flex items-center justify-center text-white hover:border-gold transition-colors"
              >
                {auth.user.picture ? (
                  <img src={auth.user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-4 h-4" />
                )}
              </button>
              <div
                className={`absolute right-0 top-full mt-2 w-48 rounded-xl border border-line bg-[rgba(7,7,10,0.96)] backdrop-blur-xl p-1.5 transition-all duration-200 ${
                  acctOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 pointer-events-none"
                }`}
                data-testid="nav-account-menu"
              >
                <Link
                  to="/account/orders"
                  onClick={() => setAcctOpen(false)}
                  data-testid="nav-my-orders"
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] text-dim hover:text-gold hover:bg-white/[0.04] transition-colors"
                >
                  <Package className="w-3.5 h-3.5" /> My Orders
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setAcctOpen(false);
                    auth.logout();
                    navigate("/");
                  }}
                  data-testid="nav-logout"
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] text-dim hover:text-red-400 hover:bg-white/[0.04] transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </div>
            </div>
          )}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 rounded-full border border-line flex items-center justify-center text-white"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* DESKTOP NESTED DROPDOWNS */}
      <NestedMega
        open={openMenu === "products"}
        groups={PRODUCT_GROUPS}
        toPrefix="/products"
        testidPrefix="mega-product"
        story={{
          eyebrow: "Products",
          headline: <>The vocabulary of <span className="text-gold">intelligent living.</span></>,
          body: "Hand-picked from world's best — re-engineered for India. Every product, beautiful before it is intelligent.",
          flagship: { to: "/dynamic-series", label: "Dynamic Series", testid: "mega-products-flagship" },
        }}
      />
      <NestedMega
        open={openMenu === "solutions"}
        groups={SOLUTION_GROUPS}
        toPrefix="/solutions"
        testidPrefix="mega-solution"
        story={{
          eyebrow: "Solutions",
          headline: <>Designed around the way you <span className="text-gold">actually live.</span></>,
          body: "By space. By need. For the professionals who specify the most thoughtful homes in India.",
        }}
      />
      <LocationsMega open={openMenu === "locations"} />

      <MobileDrawer open={mobileOpen} onBook={onBook} onClose={() => setMobileOpen(false)} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </header>
  );
}

function NavItem({ to, label, highlight, onEnter }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-${slugify(label)}`}
      onMouseEnter={onEnter}
      className={({ isActive }) =>
        `relative px-4 py-2.5 text-[13px] tracking-wide transition-colors ${
          isActive ? "text-gold" : highlight ? "text-amber-100" : "text-dim hover:text-white"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function DropdownTrigger({ label, isOpen, onEnter, testid }) {
  return (
    <button
      onMouseEnter={onEnter}
      data-testid={testid}
      className={`relative px-4 py-2.5 text-[13px] tracking-wide transition-colors inline-flex items-center gap-1 ${
        isOpen ? "text-gold" : "text-dim hover:text-white"
      }`}
    >
      {label}
      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
    </button>
  );
}

function MegaShell({ open, children }) {
  return (
    <div
      className={`hidden lg:block absolute left-0 right-0 top-full overflow-hidden transition-[max-height,opacity] duration-500 ${
        open ? "max-h-[640px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
      }`}
    >
      <div className="bg-[rgba(7,7,10,0.94)] backdrop-blur-2xl border-t border-line/60 border-b border-line/60">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10 lg:py-12">{children}</div>
      </div>
    </div>
  );
}

/**
 * NestedMega — left rail of groups + right pane of items.
 * Hovering a group on the left swaps the right pane to show its items.
 */
function NestedMega({ open, groups, toPrefix, testidPrefix, story }) {
  const [activeIdx, setActiveIdx] = useState(0);

  // Reset to first group when reopened
  useEffect(() => {
    if (open) setActiveIdx(0);
  }, [open]);

  const active = groups[activeIdx];

  return (
    <MegaShell open={open}>
      <div className="grid lg:grid-cols-[0.95fr_0.95fr_1.3fr] gap-10">
        {/* STORY */}
        <div className="space-y-5">
          <p className="eyebrow">{story.eyebrow}</p>
          <h3 className="font-display text-3xl tracking-tight leading-tight max-w-[16ch]">
            {story.headline}
          </h3>
          <p className="text-dim text-[13.5px] leading-relaxed max-w-[38ch]">{story.body}</p>
          {story.flagship && (
            <Link
              to={story.flagship.to}
              data-testid={story.flagship.testid}
              className="group inline-flex items-center gap-3 rounded-2xl border border-gold/30 bg-gold/5 p-4 hover:bg-gold/10 transition-colors w-full max-w-[280px]"
            >
              <div className="w-9 h-9 rounded-xl border border-gold/40 flex items-center justify-center text-gold">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.18em] uppercase text-amber-200">Flagship</div>
                <div className="font-display text-[15px] text-white mt-0.5">{story.flagship.label}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>

        {/* GROUP RAIL */}
        <div className="border-l border-line/50 pl-8" data-testid={`${testidPrefix}-rail`}>
          <p className="text-[10px] tracking-[0.32em] uppercase text-mute mb-4">Categories</p>
          <ul className="space-y-1">
            {groups.map((g, i) => (
              <li key={g.slug}>
                <button
                  type="button"
                  onMouseEnter={() => setActiveIdx(i)}
                  onFocus={() => setActiveIdx(i)}
                  data-testid={`${testidPrefix}-group-${g.slug}`}
                  className={`group w-full text-left flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                    i === activeIdx
                      ? "bg-gold/10 text-amber-100"
                      : "text-dim hover:text-white hover:bg-white/[0.04]"
                  }`}
                >
                  <span className="text-[13.5px] tracking-wide">{g.label}</span>
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${i === activeIdx ? "translate-x-0.5 text-gold" : "opacity-50"}`} />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* ITEMS PANE */}
        <div className="border-l border-line/50 pl-8 min-h-[280px]" data-testid={`${testidPrefix}-items`}>
          <p className="text-[10px] tracking-[0.32em] uppercase text-gold/80 mb-1.5">{active?.label}</p>
          {active?.blurb && (
            <p className="text-mute text-[12px] mb-5 max-w-[40ch]">{active.blurb}</p>
          )}
          <ul className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4">
            {active?.items.map((it) => (
              <li key={it.slug}>
                <Link
                  to={`${toPrefix}/${it.slug}`}
                  data-testid={`${testidPrefix}-${it.slug}`}
                  className="group flex items-center gap-2 text-[13.5px] text-dim hover:text-gold transition-colors py-1.5"
                >
                  <span className="w-1 h-1 rounded-full bg-current opacity-40 group-hover:opacity-100" />
                  {it.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </MegaShell>
  );
}

function LocationsMega({ open }) {
  return (
    <MegaShell open={open}>
      <div className="grid lg:grid-cols-[1fr_3fr] gap-12">
        <div className="space-y-5">
          <p className="eyebrow">Locations</p>
          <h3 className="font-display text-3xl tracking-tight leading-tight max-w-[16ch]">
            Local teams.<br /> <span className="text-gold">Citywide service.</span>
          </h3>
          <p className="text-dim text-[13.5px] leading-relaxed max-w-[38ch]">
            Dedicated installation and service teams across Telangana and Andhra Pradesh.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {LOCATION_ITEMS.map((l) => (
            <Link
              key={l.slug}
              to={`/smart-home/${l.slug}`}
              data-testid={`mega-location-${l.slug}`}
              className="group rounded-2xl border border-line bg-[var(--gx-surface)]/40 p-5 hover:border-gold/40 transition-colors"
            >
              <div className="text-[10px] tracking-[0.32em] text-mute uppercase mb-2">Service Area</div>
              <div className="font-display text-lg tracking-tight group-hover:text-gold transition-colors">{l.label}</div>
            </Link>
          ))}
        </div>
      </div>
    </MegaShell>
  );
}

function MobileDrawer({ open, onBook, onClose }) {
  const auth = useCustomerAuth();
  const { data: cmsData } = useCms();
  const navItems = mergeNavItems(cmsData?.header?.nav_items).filter((i) => i.enabled !== false);
  const preItems = navItems.filter((i) => ["home", "dynamic-series", "value-packs", "shop"].includes(i.key));
  const postItems = navItems.filter((i) => ["about", "contact"].includes(i.key));
  const contact = cmsData?.contact || {};
  const phone = contact.phone || BRAND.phone;
  const phoneRaw = phone.replace(/[^0-9]/g, "");
  const ctaLabel = cmsData?.header?.cta_label || "Experience";
  const ctaLink = cmsData?.header?.cta_link || "/experience";
  const megaMenus = mergeMegaMenus(cmsData?.header?.mega_menus);
  return (
    <div
      className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-out ${
        open ? "max-h-[80vh] overflow-y-auto" : "max-h-0"
      }`}
    >
      <div className="bg-[rgba(7,7,10,0.96)] backdrop-blur-xl border-t border-line px-5 pb-7 pt-2">
        {preItems.map((i) => (
          <MobileLink key={i.key} to={i.to} label={i.label} onClick={onClose} />
        ))}
        {auth?.user && <MobileLink to="/account/orders" label="My Orders" onClick={onClose} />}
        {megaMenus.products.enabled !== false && (
        <MobileAccordion label={megaMenus.products.label} testid="mobile-acc-products">
          {PRODUCT_GROUPS.map((g) => (
            <MobileSubAccordion key={g.slug} label={g.label} testid={`mobile-subacc-product-${g.slug}`}>
              <ul className="space-y-1.5 py-2">
                {g.items.map((it) => (
                  <li key={it.slug}>
                    <Link
                      to={`/products/${it.slug}`}
                      onClick={onClose}
                      data-testid={`mobile-product-${it.slug}`}
                      className="text-[13.5px] text-dim hover:text-gold block py-1.5"
                    >
                      {it.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </MobileSubAccordion>
          ))}
        </MobileAccordion>
        )}
        {megaMenus.solutions.enabled !== false && (
        <MobileAccordion label={megaMenus.solutions.label} testid="mobile-acc-solutions">
          {SOLUTION_GROUPS.map((g) => (
            <MobileSubAccordion key={g.slug} label={g.label} testid={`mobile-subacc-solution-${g.slug}`}>
              <ul className="space-y-1.5 py-2">
                {g.items.map((it) => (
                  <li key={it.slug}>
                    <Link
                      to={`/solutions/${it.slug}`}
                      onClick={onClose}
                      data-testid={`mobile-solution-${it.slug}`}
                      className="text-[13.5px] text-dim hover:text-gold block py-1.5"
                    >
                      {it.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </MobileSubAccordion>
          ))}
        </MobileAccordion>
        )}
        {megaMenus.locations.enabled !== false && (
        <MobileAccordion label={megaMenus.locations.label} testid="mobile-acc-locations">
          <ul className="space-y-1.5 py-2">
            {LOCATION_ITEMS.map((l) => (
              <li key={l.slug}>
                <Link
                  to={`/smart-home/${l.slug}`}
                  onClick={onClose}
                  data-testid={`mobile-location-${l.slug}`}
                  className="text-[14px] text-dim hover:text-gold block py-1.5"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </MobileAccordion>
        )}
        {postItems.map((i) => (
          <MobileLink key={i.key} to={i.to} label={i.label} onClick={onClose} />
        ))}

        <div className="pt-6 flex flex-col gap-3">
          <Link to={ctaLink} onClick={onClose} data-testid="mobile-experience-cta" className="btn-gold w-full inline-flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {ctaLabel === "Experience" ? "Live Experience" : ctaLabel}
          </Link>
          <button onClick={onBook} data-testid="mobile-book-consultation" className="btn-outline w-full">
            Book a Consultation
          </button>
          <a href={`tel:+${phoneRaw}`} className="btn-outline w-full">
            Call {phone}
          </a>
          {auth?.user && (
            <button
              type="button"
              onClick={() => {
                auth.logout();
                onClose();
              }}
              data-testid="mobile-nav-logout"
              className="btn-outline w-full"
            >
              Sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MobileLink({ to, label, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`mobile-nav-${slugify(label)}`}
      className={({ isActive }) =>
        `py-4 border-b border-line/60 flex items-center justify-between text-[15px] ${isActive ? "text-gold" : "text-white"}`
      }
    >
      <span>{label}</span>
      <ArrowRight className="w-4 h-4 text-mute" />
    </NavLink>
  );
}

function MobileAccordion({ label, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line/60">
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        className="w-full py-4 flex items-center justify-between text-[15px] text-white"
      >
        <span>{label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

function MobileSubAccordion({ label, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-l border-gold/20 pl-4 my-2">
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        className="w-full py-2.5 flex items-center justify-between text-[12px] tracking-[0.18em] uppercase text-amber-200"
      >
        <span>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

function slugify(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
